import React from "react";
import { scaleGeoPointsToSvg } from "./scaleGeoPoints";
import {
  Dumptruck,
} from "@deere/icons/new";

/* ── SVG icon primitives (same as used in MissionPanel, Tools, etc.) ── */

const Polyline: React.FC<{ points: string; stroke?: string; strokeWidth?: number }> = ({
  points,
  stroke = "black",
  strokeWidth = 2,
}) => (
  <svg width="32" height="32" viewBox="0 0 200 200">
    <polyline points={points} fill="none" stroke={stroke} strokeWidth={strokeWidth} />
  </svg>
);

const Polygon: React.FC<{ points: string; fill?: string; stroke?: string; strokeWidth?: number }> = ({
  points,
  fill = "lightblue",
  stroke = "black",
  strokeWidth = 1,
}) => (
  <svg width="32" height="32" viewBox="0 0 200 200">
    <polygon points={points} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />
  </svg>
);

const MarkerDot: React.FC = () => (
  <svg width="32" height="32" viewBox="0 0 200 200">
    <circle cx="100" cy="100" r="30" fill="#3290ff" stroke="#1a6dd4" strokeWidth="4" />
  </svg>
);

/**
 * Find a jobsite attribute (Shape, Line, or Point) by UUID from orgDataSession.
 * Returns { name, entity, type } or null if not found.
 */
export function findAttributeByUUID(
  orgDataSession: any,
  uuid: string,
): { name: string; entity: any; type: "Shape" | "Line" | "Point" } | null {
  if (!orgDataSession || !uuid) return null;

  const categories: Array<"Shape" | "Line" | "Point"> = ["Shape", "Line", "Point"];
  for (const category of categories) {
    const entries = orgDataSession[category];
    if (entries && typeof entries === "object") {
      for (const name of Object.keys(entries)) {
        const entity = entries[name];
        if (entity?.uuid === uuid) {
          return { name, entity, type: category };
        }
      }
    }
  }
  return null;
}

/**
 * Generate an icon JSX element for a resolved attribute.
 */
function iconForAttribute(entity: any, type: string): JSX.Element {
  if (type === "Shape") {
    const pts = scaleGeoPointsToSvg(entity?.points);
    return <Polygon points={pts} stroke="#3290ff" strokeWidth={4} />;
  } else if (type === "Line") {
    const pts = scaleGeoPointsToSvg(entity?.points);
    return <Polyline points={pts} stroke="#3290ff" strokeWidth={6} />;
  }
  return <MarkerDot />;
}

/**
 * Default icon for Command tasks (matches the Dumptruck icon used in MissionPanel).
 */
const commandIcon = (
  <Dumptruck style={{ minHeight: "32px", minWidth: "32px", height: "auto", width: "auto" }} />
);

/**
 * Resolve UUID references in mission tasks to full location data from orgDataSession.
 *
 * For each Navigate task that has `properties.uuid` but no `navigateItem`,
 * looks up the matching Shape/Line/Point in orgDataSession and enriches
 * the task with the full properties (points, location, zoneType, etc.),
 * a navigateItem object, and an icon for display.
 *
 * For Command tasks that have no icon, assigns the default command icon.
 *
 * Does NOT modify the original tasks — returns new task objects.
 */
export function resolveTaskLocations(tasks: any[], orgDataSession: any): any[] {
  if (!tasks || !orgDataSession) return tasks || [];

  return tasks.map((task) => {
    // Resolve Command tasks: ensure they always have an icon
    if (task.action === "Command") {
      if (task.icon) return task;
      return { ...task, icon: commandIcon };
    }

    // Only resolve Navigate tasks that have a UUID but no navigateItem
    if (task.action !== "Navigate" || !task.properties?.uuid) return task;
    if (task.navigateItem) return task; // Already resolved

    const found = findAttributeByUUID(orgDataSession, task.properties.uuid);
    if (!found) return task; // UUID not found in current jobsite

    const { name, entity, type } = found;
    const icon = iconForAttribute(entity, type);

    return {
      ...task,
      title: task.title || name,
      icon,
      navigateItem: {
        title: name,
        subtitle: entity.zoneType || entity.pathType || entity.pointType || type,
        icon,
        properties: entity,
      },
      properties: entity,
    };
  });
}
